import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const parse = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const fmt = (d) => (d ? format(d, "yyyy-MM-dd") : "");

const triggerCls = "w-full rounded-xl h-11 mt-1 px-3.5 flex items-center gap-2 text-left border bg-background transition-colors hover:border-[var(--tenant-accent)]";

export function DateField({ value, onChange, placeholder = "Pick a date", testid }) {
  const [open, setOpen] = useState(false);
  const d = parse(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={triggerCls}>
          <CalendarIcon className="h-4 w-4 shrink-0 accent-text" />
          <span className={d ? "text-[#2C2C3E]" : "text-muted-foreground"}>{d ? format(d, "EEE, MMM d, yyyy") : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
        <Calendar mode="single" selected={d} defaultMonth={d}
          onSelect={(nd) => { onChange(fmt(nd)); setOpen(false); }} />
        {value && (
          <button type="button" data-testid={testid ? `${testid}-clear` : undefined}
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-center text-xs font-semibold text-muted-foreground hover:text-destructive py-2.5 border-t">Clear date</button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function DateRangeField({ from, to, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const range = { from: parse(from), to: parse(to) };
  const label = range.from
    ? `${format(range.from, "MMM d")}${range.to ? ` → ${format(range.to, "MMM d, yyyy")}` : " → …"}`
    : "Pick arrival & departure";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={triggerCls}>
          <CalendarIcon className="h-4 w-4 shrink-0 accent-text" />
          <span className={range.from ? "text-[#2C2C3E]" : "text-muted-foreground"}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
        <Calendar mode="range" selected={range} defaultMonth={range.from}
          onSelect={(r) => onChange({ from: fmt(r?.from), to: fmt(r?.to) })} numberOfMonths={1} />
        <div className="flex items-center justify-between px-3 py-2.5 border-t">
          <button type="button" data-testid={testid ? `${testid}-clear` : undefined}
            onClick={() => { onChange({ from: "", to: "" }); }}
            className="text-xs font-semibold text-muted-foreground hover:text-destructive">Clear</button>
          <button type="button" data-testid={testid ? `${testid}-done` : undefined}
            onClick={() => setOpen(false)}
            className="text-xs font-bold accent-text">Done</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
